import React, { createContext, useState, ReactNode, useContext, useCallback, useEffect, useRef } from 'react';
import enLocale from '../public/locales/en.json';

// ─── Language list (stays in main bundle — needed by Header switcher UI) ─────
export const languages = [
    { code: 'en', name: 'English' },
    { code: 'sr', name: 'Srpski' },
    { code: 'de', name: 'Deutsch' },
    { code: 'it', name: 'Italiano' },
    { code: 'el', name: 'Ελληνικά' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'ru', name: 'Русский' },
    { code: 'zh-CN', name: '中文' },
    { code: 'ar', name: 'العربية' },
    { code: 'fa', name: 'فارسی' },
    { code: 'ur', name: 'اُردو' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'tl', name: 'Tagalog' },
];

const VALID_CODES = new Set(languages.map(l => l.code));

const normalizeUrlLanguage = (value?: string | null) => {
    if (/^(cn|zh-cn)$/i.test(value || '')) return 'zh-CN';
    return value || 'en';
};

const getLanguageFromLocation = () => {
    if (typeof window === 'undefined') return 'en';

    const blogLanguageMatch = window.location.pathname.match(/^\/blog\/[^/]+\/([a-z]{2}(?:-[A-Za-z]+)?)\/?$/i);
    const blogLanguage = normalizeUrlLanguage(blogLanguageMatch?.[1]);
    if (blogLanguageMatch && VALID_CODES.has(blogLanguage)) return blogLanguage;

    const langParam = new URLSearchParams(window.location.search).get('lang');
    const queryLanguage = normalizeUrlLanguage(langParam);
    return langParam && VALID_CODES.has(queryLanguage) ? queryLanguage : 'en';
};

// ─── Translation loader ───────────────────────────────────────────────────────
// In-memory cache so switching back to a previously-loaded language is instant.
const translationCache = new Map<string, any>();

async function fetchTranslation(code: string): Promise<any> {
    if (translationCache.has(code)) {
        return translationCache.get(code);
    }
    const res = await fetch(`/locales/${code}.json`);
    if (!res.ok) throw new Error(`Failed to fetch /locales/${code}.json: ${res.status}`);
    const data = await res.json();
    translationCache.set(code, data);
    return data;
}

// ─── Language Context ─────────────────────────────────────────────────────────
interface LanguageContextType {
    language: string;
    setLanguage: (language: string) => void;
    t: any;
    languages: { code: string; name: string }[];
    isLoading: boolean;
}

export const LanguageContext = createContext<LanguageContextType>({
    language: 'en',
    setLanguage: () => { },
    t: {},
    languages,
    isLoading: true,
});

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Resolve initial language from URL params (same logic as before)
    const [language, setLanguageState] = useState<string>(getLanguageFromLocation);

    // Initialise with bundled English locale so the page renders immediately (no fetch delay)
    const [t, setT] = useState<any>(enLocale);
    const [isLoading, setIsLoading] = useState(language === 'en');
    // Track the latest request so a slow response doesn't overwrite a newer one
    const requestIdRef = useRef(0);

    useEffect(() => {
        document.documentElement.lang = language;
        document.documentElement.dir = ['ar', 'fa', 'ur'].includes(language) ? 'rtl' : 'ltr';
    }, [language]);

    useEffect(() => {
        const syncFromUrl = () => {
            const newLang = getLanguageFromLocation();
            setLanguageState((prev) => (prev !== newLang ? newLang : prev));
        };
        window.addEventListener('popstate', syncFromUrl);
        return () => window.removeEventListener('popstate', syncFromUrl);
    }, []);

    // Fetch the active language's JSON whenever it changes
    useEffect(() => {
        let cancelled = false;
        const id = ++requestIdRef.current;

        setIsLoading(true);

        fetchTranslation(language)
            .then(data => {
                if (cancelled || id !== requestIdRef.current) return;
                setT(data);
                setIsLoading(false);
            })
            .catch(async () => {
                // Fallback: try English if the requested language fails
                if (cancelled || id !== requestIdRef.current) return;
                try {
                    const fallback = await fetchTranslation('en');
                    if (cancelled || id !== requestIdRef.current) return;
                    setT(fallback);
                } catch {
                    // en.json also failed — leave t as null; UI will render with empty strings
                }
                setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, [language]);

    const setLanguage = useCallback((lang: string) => {
        const newLang = VALID_CODES.has(lang) ? lang : 'en';
        setLanguageState(newLang);

        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            const blogPostMatch = url.pathname.match(/^\/blog\/([^/]+)(?:\/[a-z]{2}(?:-[A-Za-z]+)?)?\/?$/i);

            if (blogPostMatch) {
                const slug = blogPostMatch[1];
                url.pathname = newLang === 'en' ? `/blog/${slug}` : `/blog/${slug}/${newLang}`;
                url.searchParams.delete('lang');
                window.history.pushState({}, '', url);
                window.dispatchEvent(new PopStateEvent('popstate'));
            } else {
                if (newLang === 'en') {
                    url.searchParams.delete('lang');
                } else {
                    url.searchParams.set('lang', newLang);
                }
                window.history.pushState({}, '', url);
            }
        }
    }, []);

    // Never block render — enLocale is always available as the initial state.
    // Other languages load async and swap in once fetched.

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t, languages, isLoading }}>
            {children}
        </LanguageContext.Provider>
    );
};

// ─── Navigation Context ───────────────────────────────────────────────────────
interface NavigationContextType {
    path: string;
    navigate: (newPath: string) => void;
}

const NavigationContext = createContext<NavigationContextType>({
    path: '/',
    navigate: () => { },
});

export const useNavigation = () => useContext(NavigationContext);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const getInitialPath = () => {
        const hostname = window.location.hostname;
        if (hostname.includes('craft-code')) {
            return '/craft-code';
        }
        if (window.location.pathname && window.location.pathname !== '/') {
            return window.location.pathname;
        }
        return window.location.hash.substring(1) || '/';
    };

    const [path, setPath] = useState(getInitialPath);

    const handleHashChange = useCallback(() => {
        if (window.location.pathname && window.location.pathname !== '/') return;
        setPath(window.location.hash.substring(1) || '/');
    }, []);

    const handlePopState = useCallback(() => {
        if (window.location.pathname && window.location.pathname !== '/') {
            setPath(window.location.pathname);
        } else {
            setPath(window.location.hash.substring(1) || '/');
        }
    }, []);

    useEffect(() => {
        window.addEventListener('hashchange', handleHashChange);
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [handleHashChange, handlePopState]);

    const navigate = (newPath: string) => {
        if (!newPath) return;

        const destination = newPath.startsWith('/') ? newPath : `/${newPath}`;
        const currentLanguage = new URLSearchParams(window.location.search).get('lang');
        const blogPostHasLanguage = /^\/blog\/[^/]+\/[a-z]{2}(?:-[A-Za-z]+)?\/?$/i.test(destination);
        const languageQuery = currentLanguage && !blogPostHasLanguage ? `?lang=${encodeURIComponent(currentLanguage)}` : '';

        if (destination === '/') {
            window.history.pushState(null, '', `/${languageQuery}`);
            setPath('/');
            window.scrollTo(0, 0);
        } else {
            window.history.pushState(null, '', `${destination}${languageQuery}`);
            setPath(destination);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <NavigationContext.Provider value={{ path, navigate }}>
            {children}
        </NavigationContext.Provider>
    );
};
